/**
 * LYT Communications - Photo Upload Service
 * Version: 2.0.0
 * Updated: 2026-06-05
 *
 * Handles photo uploads to Google Drive via GAS.
 * Folder structure: LYT Field Photos / {Project} / {Section} / {Category} / {date}_{author}_{file}.jpg
 * Falls back to localStorage cache when offline.
 */

// Onboarding handler GAS — has driveUpload action
const DRIVE_UPLOAD_URL = 'https://script.google.com/macros/s/AKfycbwuBfWDZegdtnFloF_CehxiVI129RlEL9DEEt108r9HLYJopAMDvFYvmmOvhz4_rcGRQQ/exec';
const PHOTOS_FOLDER = '1qrj3Rkmt2z3IfGhX2_q5XlgRlh_gLMQY';

// Helper: POST to GAS with redirect following
async function gasPost(url, payload) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    // GAS sometimes returns an HTML redirect page
    if (text.includes('<HTML>') || text.includes('HREF=')) {
      const match = text.match(/HREF="([^"]+)"/i);
      if (match) {
        const redirectUrl = match[1].replace(/&amp;/g, '&');
        const redirectResponse = await fetch(redirectUrl, { mode: 'cors' });
        return await redirectResponse.json();
      }
    }
    try { return JSON.parse(text); } catch (e) { return { result: text }; }
  } catch (err) {
    console.error('[photoService] GAS error:', err);
    return null;
  }
}

/**
 * Convert a File object to base64 string
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Build a clean folder name (strip special chars, title-case)
 */
function cleanName(str) {
  if (!str) return 'General';
  return str.replace(/[/\\:*?"<>|]/g, '').replace(/\s+/g, ' ').trim() || 'General';
}

/**
 * Build a date prefix for filenames (YYYY-MM-DD)
 */
function datePrefix() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Upload a single photo to Google Drive with smart folder structure.
 *
 * Folder structure:
 *   {Project Name} / {Section} / {Category} / files...
 *
 * File naming:
 *   {YYYY-MM-DD}_{author}_{photoType}_{index}.jpg
 *
 * Also writes/updates an _info.txt in the category folder with pin/device/segment details.
 *
 * @param {File} file - The file to upload
 * @param {object} ctx - Context for folder structure
 * @param {string} ctx.projectName - Human-readable project name (e.g. "Bastrop Fiber")
 * @param {string} ctx.projectId - Project DB ID (fallback if no name)
 * @param {string} ctx.section - Section name (e.g. "Section 1A")
 * @param {string} ctx.category - Pin category (safety, progress, issue, general)
 * @param {string} ctx.authorName - Who took the photo
 * @param {string} ctx.noteId - Pin/note ID
 * @param {string} ctx.noteText - Pin description text
 * @param {string} ctx.status - Pin status
 * @param {string} ctx.photoType - Label (pin_photo, status_photo, etc.)
 * @param {object} ctx.linkedItem - Linked segment/device info { type, name, id, status }
 * @returns {object} { success, fileId, url, name }
 */
export async function uploadPhoto(file, ctx = {}) {
  const projectFolder = cleanName(ctx.projectName || ctx.projectId || 'General');
  const sectionFolder = cleanName(ctx.section || 'Unassigned');
  const categoryFolder = cleanName((ctx.category || 'general').charAt(0).toUpperCase() + (ctx.category || 'general').slice(1));
  const author = cleanName(ctx.authorName || 'Unknown').replace(/\s+/g, '-');
  const date = datePrefix();
  const photoType = ctx.photoType || 'photo';
  const ext = file.name.split('.').pop() || 'jpg';
  const timestamp = Date.now().toString(36); // short unique suffix
  const fileName = `${date}_${author}_${photoType}_${timestamp}.${ext}`;
  const subfolder = `${projectFolder}/${sectionFolder}/${categoryFolder}`;

  try {
    const base64Data = await fileToBase64(file);

    // Upload photo
    const result = await gasPost(DRIVE_UPLOAD_URL, {
      action: 'driveUpload',
      params: {
        folderId: PHOTOS_FOLDER,
        fileName,
        mimeType: file.type || 'image/jpeg',
        base64Data,
        subfolder,
      },
    });

    if (result && result.fileId) {
      // Write/update _info.txt with pin/device/segment details (fire-and-forget)
      writeInfoFile(subfolder, ctx).catch(() => {});

      return {
        success: true,
        fileId: result.fileId,
        url: `https://drive.google.com/file/d/${result.fileId}/view`,
        name: fileName,
      };
    }

    console.warn('[photoService] driveUpload failed, caching locally');
    return cachePhotoLocally(file, ctx, base64Data);
  } catch (err) {
    console.error('[photoService] Upload failed:', err);
    return cachePhotoLocally(file, ctx);
  }
}

/**
 * Write an _info.txt file in the category folder with all context about the pin/item.
 * Overwrites each time so the file always has latest info.
 */
async function writeInfoFile(subfolder, ctx) {
  const lines = [
    `LYT Field Photo Info`,
    `====================`,
    `Generated: ${new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })} CST`,
    ``,
    `Project:  ${ctx.projectName || ctx.projectId || 'Unknown'}`,
    `Section:  ${ctx.section || 'Unassigned'}`,
    `Category: ${ctx.category || 'general'}`,
    ``,
    `--- Pin Details ---`,
    `Pin ID:   ${ctx.noteId || 'N/A'}`,
    `Status:   ${ctx.status || 'N/A'}`,
    `Author:   ${ctx.authorName || 'Unknown'}`,
    `Text:     ${ctx.noteText || '(no description)'}`,
  ];

  if (ctx.linkedItem) {
    lines.push(
      ``,
      `--- Linked ${ctx.linkedItem.type || 'Item'} ---`,
      `Name:     ${ctx.linkedItem.name || 'N/A'}`,
      `ID:       ${ctx.linkedItem.id || 'N/A'}`,
      `Status:   ${ctx.linkedItem.status || 'N/A'}`,
      `Type:     ${ctx.linkedItem.deviceType || ctx.linkedItem.segmentType || 'N/A'}`,
    );
  }

  if (ctx.lat && ctx.lng) {
    lines.push(``, `GPS:      ${ctx.lat}, ${ctx.lng}`);
    lines.push(`Maps:     https://www.google.com/maps?q=${ctx.lat},${ctx.lng}`);
  }

  const infoContent = lines.join('\n');
  const base64 = btoa(unescape(encodeURIComponent(infoContent)));

  await gasPost(DRIVE_UPLOAD_URL, {
    action: 'driveUpload',
    params: {
      folderId: PHOTOS_FOLDER,
      fileName: '_info.txt',
      mimeType: 'text/plain',
      base64Data: base64,
      subfolder,
    },
  });
}

/**
 * Upload multiple photos for a workflow step
 * @param {Array<{file: File, type: string}>} photos - Array of photo objects
 * @param {object} ctx - Context object (same as uploadPhoto)
 * @param {function} onProgress - Progress callback (index, total)
 * @returns {Array<object>} Results array
 */
export async function uploadPhotoBatch(photos, ctx, onProgress) {
  const results = [];
  for (let i = 0; i < photos.length; i++) {
    if (onProgress) onProgress(i + 1, photos.length);
    const result = await uploadPhoto(photos[i].file, { ...ctx, photoType: photos[i].type || `photo_${i + 1}` });
    results.push(result);
  }
  return results;
}

/**
 * Cache photo locally when offline
 */
function cachePhotoLocally(file, ctx, base64Data) {
  try {
    const key = `photo_cache_${ctx.noteId || 'unknown'}_${ctx.photoType || 'photo'}_${Date.now()}`;
    const cacheEntry = {
      name: file.name,
      type: file.type,
      size: file.size,
      ctx,
      timestamp: new Date().toISOString(),
      data: base64Data && base64Data.length < 2 * 1024 * 1024 ? base64Data : null,
      synced: false,
    };
    localStorage.setItem(key, JSON.stringify(cacheEntry));
    console.log(`[photoService] Cached locally: ${key}`);
    return {
      success: true,
      cached: true,
      name: file.name,
      url: null,
      localKey: key,
    };
  } catch (err) {
    console.error('[photoService] Cache failed:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get pending (unsynced) photos from local cache
 */
export function getPendingPhotos() {
  const pending = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('photo_cache_')) {
      try {
        const entry = JSON.parse(localStorage.getItem(key));
        if (!entry.synced) {
          pending.push({ key, ...entry });
        }
      } catch (e) {
        // skip corrupt entries
      }
    }
  }
  return pending;
}

/**
 * Sync pending cached photos when back online
 */
export async function syncPendingPhotos(onProgress) {
  const pending = getPendingPhotos();
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (let i = 0; i < pending.length; i++) {
    const entry = pending[i];
    if (onProgress) onProgress(i + 1, pending.length);

    if (entry.data) {
      // Reconstruct folder path from cached context
      const ctx = entry.ctx || {};
      const projectFolder = cleanName(ctx.projectName || ctx.projectId || 'General');
      const sectionFolder = cleanName(ctx.section || 'Unassigned');
      const cat = ctx.category || 'general';
      const categoryFolder = cleanName(cat.charAt(0).toUpperCase() + cat.slice(1));
      const subfolder = `${projectFolder}/${sectionFolder}/${categoryFolder}`;
      const author = cleanName(ctx.authorName || 'Unknown').replace(/\s+/g, '-');
      const date = entry.timestamp ? entry.timestamp.slice(0, 10) : datePrefix();

      const result = await gasPost(DRIVE_UPLOAD_URL, {
        action: 'driveUpload',
        params: {
          folderId: PHOTOS_FOLDER,
          fileName: `${date}_${author}_${ctx.photoType || 'photo'}_${Date.now().toString(36)}.jpg`,
          mimeType: entry.type || 'image/jpeg',
          base64Data: entry.data,
          subfolder,
        },
      });

      if (result && result.fileId) {
        entry.synced = true;
        entry.fileId = result.fileId;
        localStorage.setItem(entry.key, JSON.stringify(entry));
        synced++;
      } else {
        failed++;
      }
    } else {
      failed++; // No base64 data cached, can't sync
    }
  }

  return { synced, failed, total: pending.length };
}

const photoService = {
  uploadPhoto,
  uploadPhotoBatch,
  getPendingPhotos,
  syncPendingPhotos,
};
export default photoService;
